import type { ProjectBrief } from '@dyspatch/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { ExternalLink, GitPullRequest, PartyPopper, ShieldCheck } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import {
  Age,
  PrMark,
  prNumber,
  RepoChip,
  RowSkeleton,
  SizeChip,
  SourceChip,
  TaskKey,
} from '@/components/common/bits';
import { Badge } from '@/components/ui/badge';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { PlanActions, ReviewActions } from '@/features/actions/GateActions';
import { RunSummary } from '@/features/tasks/TaskParts';
import { useTasks } from '@/hooks/use-fleet';
import { type RowProps, useRowKeys } from '@/hooks/use-row-keys';
import type { ReviewEntry } from '@/lib/api';
import { gateCounts } from '@/lib/fleet';
import { minutesSince } from '@/lib/format';
import { shortKey } from '@/lib/keys';
import { unmergedDependencies } from '@/lib/merge-order';
import { cn } from '@/lib/utils';

/**
 * The gates, and the one interaction decision in this design worth arguing
 * about.
 *
 * Three arrangements have now been tried on this list. The first drew every
 * gate's five buttons at rest, so four gates was twenty controls and the page
 * read as a form rather than as four decisions. The second collapsed all of them
 * to one line and expanded the oldest, which fixed the button count but left the
 * decision the product is *for* rendered at the same 13px as everything else on
 * the page -- a plan approval looked exactly like a row in a table of repos.
 *
 * This one keeps the collapse and spends the space it saved. The gate you should
 * look at next is a full-size card: the title at 28px, the plan in prose, the
 * verbs at a size a thumb can hit, on a surface washed in the accent so it is
 * visibly a different kind of thing from the lists below it. The others stay one
 * line each -- repo, source, who filed it, how long it has waited -- and a tap or
 * Enter promotes any of them.
 *
 * The keyboard is unchanged from the previous version, deliberately: `e` and `x`
 * press the open gate's own buttons rather than calling the hub, so the reason
 * field a send-back cannot skip and the disabled state of a write already in
 * flight hold for a keystroke exactly as they do for a click.
 *
 * The empty and loading states get the opposite treatment. When there is no gate
 * there is nothing to spend space on, so both collapse to about one row: the
 * hero one region above already carries the zero in 60px type, and a second
 * full-size statement of it is the same fact told twice.
 */
function ClosedRow({
  onOpen,
  icon: Icon,
  title,
  href,
  meta,
  age,
  stale,
  rowProps,
}: {
  onOpen: () => void;
  /** Which *kind* of gate this is, not the shared state icon: every row here
   * is already `review` or `awaiting_approval`, so the state vocabulary
   * would draw the same `ShieldCheck` for both and a reader could no longer
   * tell a plan from a pull request in one merged list. Chosen so the two
   * kinds stay shape-distinct instead. */
  icon: typeof ShieldCheck;
  title: string;
  href: ReactNode;
  meta: ReactNode;
  age: ReactNode;
  stale?: boolean;
  rowProps: RowProps;
}) {
  return (
    <li
      className={cn(
        'hover:bg-raised/60 relative transition-colors duration-100',
        stale && 'bg-bad-bg/25',
      )}
    >
      {/* The whole row promotes the gate, not a 14px chevron. The button is
          stretched over the row and the content sits above it with
          `pointer-events-none`, which lets every link keep its own click and
          sends the rest to the toggle. 44px tall on a phone. */}
      <div className="relative flex items-start gap-3 px-gutter py-3 max-sm:min-h-11">
        <button
          type="button"
          {...rowProps}
          onClick={onOpen}
          className="absolute inset-0"
          aria-label={`Open ${title}`}
          title={title}
        />
        <Icon
          className={cn(
            'pointer-events-none mt-0.5 size-4 shrink-0',
            stale ? 'text-bad' : 'text-gate',
          )}
          aria-hidden
        />
        <div className="pointer-events-none relative min-w-0 flex-1 [&_a]:pointer-events-auto">
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-base">{href}</span>
            {stale ? <Badge variant="destructive">stale</Badge> : null}
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">{meta}</div>
        </div>
        <span className="relative mt-0.5 shrink-0">{age}</span>
      </div>
    </li>
  );
}

/** The one gate that is open: the page's subject, at the page's largest size. */
function OpenGate({
  eyebrow,
  title,
  href,
  meta,
  age,
  stale,
  children,
  actions,
}: {
  eyebrow: string;
  title: string;
  href: ReactNode;
  meta: ReactNode;
  age: ReactNode;
  stale?: boolean;
  children?: ReactNode;
  actions: ReactNode;
}) {
  return (
    <div className="dy-rise px-gutter pt-5 pb-1">
      <div className="flex items-center gap-2.5">
        <span className="dy-eyebrow text-gate">{eyebrow}</span>
        {stale ? <Badge variant="destructive">stale</Badge> : null}
        <span className="ml-auto shrink-0">{age}</span>
      </div>
      <h3 className="mt-1.5 text-2xl leading-tight font-semibold tracking-[-0.022em]">
        <span className="[&_a]:hover:text-gate [&_a]:transition-colors [&_a]:duration-100">
          {href}
        </span>
      </h3>
      <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">{meta}</div>
      {children}
      <div className="-mx-gutter mt-3">{actions}</div>
      <span className="sr-only">{title}</span>
    </div>
  );
}

/**
 * A plan waiting for a Go, open or closed.
 *
 * Split out of the list rather than built inline in the map so the row's parts
 * are named once and the two states share them. Both branches read the same
 * `href`, `meta` and `age`, which is what keeps a closed row and the open card
 * saying the same things about the same job.
 */
function PlanGate({
  brief,
  open,
  onOpen,
  rowProps,
  staleAfterMinutes,
  className,
}: {
  brief: ProjectBrief;
  open: boolean;
  onOpen: () => void;
  rowProps: RowProps;
  staleAfterMinutes?: number;
  className?: string;
}) {
  const age = (
    <Age minutes={minutesSince(brief.updatedAt)} hot staleAfterMinutes={staleAfterMinutes} />
  );
  const href = (
    <Link to="/jobs/$briefId" params={{ briefId: brief.id }}>
      {brief.title}
    </Link>
  );
  const meta = (
    <>
      <TaskKey>{shortKey(brief.id)}</TaskKey>
      <RepoChip repo={brief.repos[0] ?? '-'} extra={brief.repos.length - 1} />
      <SourceChip source={brief.source} />
      <span className="text-fg-3 truncate text-xs">by {brief.submittedBy}</span>
    </>
  );

  if (!open) {
    return (
      <ul className={className}>
        <ClosedRow
          onOpen={onOpen}
          rowProps={rowProps}
          icon={ShieldCheck}
          title={brief.title}
          href={href}
          meta={meta}
          age={age}
        />
      </ul>
    );
  }

  return (
    <div data-gate-id={brief.id} className={className}>
      <OpenGate
        eyebrow="Plan approval"
        title={brief.title}
        href={href}
        meta={meta}
        age={age}
        actions={<PlanActions briefId={brief.id} />}
      >
        <p className="text-fg-2 mt-3 max-w-prose text-base">
          {brief.summary ||
            'The plan is drafted and waiting for a person to approve it. Nothing runs until you say go.'}
        </p>
      </OpenGate>
    </div>
  );
}

/** A pull request waiting for a review decision, open or closed. */
function ReviewGate({
  entry,
  waitsOn,
  open,
  onOpen,
  rowProps,
  staleAfterMinutes,
  className,
}: {
  entry: ReviewEntry;
  /** This entry's dependencies that have not merged yet (issue #24). */
  waitsOn: readonly { key: string; prUrl: string | null }[];
  open: boolean;
  onOpen: () => void;
  rowProps: RowProps;
  staleAfterMinutes?: number;
  className?: string;
}) {
  const age = <Age minutes={entry.waitingMinutes} hot staleAfterMinutes={staleAfterMinutes} />;
  const href = (
    <Link to="/tasks/$taskId" params={{ taskId: entry.id }}>
      {entry.title}
    </Link>
  );
  const meta = (
    <>
      <TaskKey briefId={entry.briefId}>{entry.key}</TaskKey>
      <RepoChip repo={entry.repo} />
      {/* The size chip every other list of tasks already draws (issue #64);
          this one was the last row shape still missing it. */}
      <SizeChip size={entry.size} />
      <span className="text-fg-3 truncate text-xs">
        {entry.workerName || entry.ownerName || entry.ownerEmail || '-'}
      </span>
      {entry.attempts > 1 ? (
        <span className="text-fg-3 text-xs">attempt {entry.attempts}</span>
      ) : null}
      {entry.prUrl ? (
        <a
          href={entry.prUrl}
          target="_blank"
          rel="noreferrer"
          className="text-fg-3 hover:text-gate inline-flex items-center gap-1 text-xs transition-colors duration-100"
        >
          PR{prNumber(entry.prUrl)}
          <ExternalLink className="size-3" aria-hidden />
        </a>
      ) : (
        // Closed or open, the row says which of the two this gate is: a pull
        // request to read, or a branch with none. The gate's own card carries
        // the reason and the command (see `NoPullRequest`, GateActions.tsx).
        <PrMark branch={entry.branch} />
      )}
      {waitsOn.length > 0 ? <Badge variant="gate">not mergeable yet</Badge> : null}
    </>
  );

  if (!open) {
    return (
      <ul className={className}>
        <ClosedRow
          onOpen={onOpen}
          rowProps={rowProps}
          icon={GitPullRequest}
          title={entry.title}
          href={href}
          meta={meta}
          age={age}
          stale={entry.stale}
        />
      </ul>
    );
  }

  return (
    <div data-gate-id={entry.id} className={className}>
      <OpenGate
        eyebrow="Pull request"
        title={entry.title}
        href={href}
        meta={meta}
        age={age}
        stale={entry.stale}
        actions={
          <ReviewActions
            taskId={entry.id}
            prUrl={entry.prUrl}
            branch={entry.branch}
            runSummary={entry.runSummary}
            waitsOn={waitsOn}
            // The queue offers the same verbs the task page does: a gate whose
            // PR cannot be merged should not lead with Open PR just because it
            // is being read from a list.
            pr={entry}
          />
        }
      >
        {entry.reviewFeedback ? (
          <p className="text-fg-2 mt-3 max-w-prose text-base whitespace-pre-wrap">
            <span className="text-fg-3">Previously sent back: </span>
            {entry.reviewFeedback}
          </p>
        ) : null}
        <RunSummary summary={entry.runSummary} taskId={entry.id} />
      </OpenGate>
    </div>
  );
}

export function GateStack({
  briefs,
  reviews,
  loading,
  staleAfterMinutes,
}: {
  briefs: ProjectBrief[];
  reviews: ReviewEntry[];
  loading: boolean;
  /** The hub's own stale interval, so these ages read like the hero above. */
  staleAfterMinutes?: number;
}) {
  const plans = [...briefs]
    .filter((brief) => brief.status === 'awaiting_approval')
    .sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt));
  // The same derivation the rail and the hero quote, so the heading cannot say a
  // different number from the count beside the nav.
  const total = gateCounts(briefs, reviews).total;
  /**
   * Whether each review is safe to merge yet (issue #24), off the same capped
   * task list the rest of the console reads siblings from. A dependency this
   * list has not loaded reads as unmerged rather than as clear.
   */
  const tasks = useTasks();
  const waitsOnByEntry = new Map(
    reviews.map((entry) => {
      const siblings = (tasks.data ?? []).filter((sibling) => sibling.briefId === entry.briefId);
      const statusByKey = new Map(siblings.map((sibling) => [sibling.key, sibling.status]));
      const waitsOn = unmergedDependencies(entry.dependsOn, statusByKey).map((key) => ({
        key,
        prUrl: siblings.find((sibling) => sibling.key === key)?.prUrl ?? null,
      }));
      return [entry.id, waitsOn];
    }),
  );
  const first = plans[0]?.id ?? reviews[0]?.id ?? null;
  const [open, setOpen] = useState<string | null>(first);

  // Keep the open gate open, but adopt the new oldest one when the one that was
  // open is cleared, which is what happens the moment you approve it.
  useEffect(() => {
    setOpen((previous) => {
      if (
        previous &&
        (plans.some((brief) => brief.id === previous) ||
          reviews.some((entry) => entry.id === previous))
      ) {
        return previous;
      }
      return first;
    });
  }, [first, plans, reviews]);

  const navigate = useNavigate();
  const [pressing, setPressing] = useState<{ id: string; verb: 'primary' | 'secondary' } | null>(
    null,
  );

  useEffect(() => {
    if (!pressing) return;
    const row = document.querySelector(`[data-gate-id="${CSS.escape(pressing.id)}"]`);
    row?.querySelector<HTMLElement>(`[data-gate-verb="${pressing.verb}"]`)?.click();
    setPressing(null);
  }, [pressing]);

  const { rowProps } = useRowKeys({
    ids: [...plans.map((brief) => brief.id), ...reviews.map((entry) => entry.id)],
    onEnter: (id) => {
      void (plans.some((brief) => brief.id === id)
        ? navigate({ to: '/jobs/$briefId', params: { briefId: id } })
        : navigate({ to: '/tasks/$taskId', params: { taskId: id } }));
    },
    onVerb: (verb, id) => {
      setOpen(id);
      setPressing({ id, verb });
    },
  });

  if (loading && !total) {
    // Two rows, not three: the skeleton is a promise about height, and promising
    // three gates before anybody knows there is even one makes the page jump.
    return (
      <Section>
        <SectionHead title="Waiting on you" />
        <Surface>
          <RowSkeleton rows={2} />
        </Surface>
      </Section>
    );
  }

  if (!total) {
    // One line, not a card. The hero one region above has already said the same
    // zero at 60px, so a 12rem centred restatement of it is the same fact told
    // twice and it is the largest thing on a quiet page. No gate wash and no
    // raise either: a region with nothing at its gate should not be dressed as
    // the subject of the page. The clause truncates rather than wrapping, which
    // is what keeps this at one row on a narrow window.
    return (
      <Section>
        <SectionHead title="Waiting on you" meta="clear" />
        <Surface>
          <div className="flex items-center gap-2.5 px-gutter py-3.5 max-sm:min-h-11">
            <PartyPopper className="text-fg-3 size-4 shrink-0" aria-hidden />
            <span className="text-foreground shrink-0 text-base">Nothing is waiting on you</span>
            <span className="text-fg-3 min-w-0 truncate text-sm">
              Plan approvals and pull request reviews land here, oldest first.
            </span>
          </div>
        </Surface>
      </Section>
    );
  }

  // One flat list, gates in the order they should be looked at. The old page
  // split plans and pull requests into two labelled groups, which meant a person
  // with one of each read two headings to find two rows, and the "oldest first"
  // promise was true inside each group and false down the page.
  const rows = [
    ...plans.map((brief) => ({ kind: 'plan' as const, id: brief.id, brief })),
    ...reviews.map((entry) => ({ kind: 'review' as const, id: entry.id, entry })),
  ];

  return (
    <Section>
      <SectionHead
        title="Waiting on you"
        meta={`${total} open · oldest first`}
        hue="var(--dy-gate)"
      />
      <Surface tone="gate" raised>
        {rows.map((row, index) => {
          const divider = index > 0 ? 'border-line border-t' : undefined;
          return row.kind === 'plan' ? (
            <PlanGate
              key={row.id}
              brief={row.brief}
              open={open === row.id}
              onOpen={() => setOpen(row.id)}
              rowProps={rowProps(row.id)}
              staleAfterMinutes={staleAfterMinutes}
              className={divider}
            />
          ) : (
            <ReviewGate
              key={row.id}
              entry={row.entry}
              waitsOn={waitsOnByEntry.get(row.id) ?? []}
              open={open === row.id}
              onOpen={() => setOpen(row.id)}
              rowProps={rowProps(row.id)}
              staleAfterMinutes={staleAfterMinutes}
              className={divider}
            />
          );
        })}
      </Surface>
    </Section>
  );
}
